import { FlatHierarchy, IMinUserDepricated, IPNSProps } from "@mctypes/global.types";
import { IIssueApi, ITableTemplate } from "@schemas/issueSchema";
import { IProjectMemberApiItem } from "@schemas/projectSchema";
import { ReactFlowProvider } from "@xyflow/react";
import { AgGridReact } from "ag-grid-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useGetMcbimSpacesQuery, useGetProjectByProjectIdIssuesOnBoardQuery } from "../../../../../openapi";
import { FailedAlert } from "../../../../components";
import HierarchyTreeSelector from "../../../../components/HierarchyTreeSelector/HierarchyTreeSelector";
import { addAppModalThunk } from "../../../../global/globalSlice";
import { selectCurrentProjectId } from "../../../../global/globalSliceCommon";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { IssueViewEnum } from "../../../../mc-shared/enums";
import { AppModalEnums, MCApplicationsUrlEnum } from "../../../../mc-shared/sharedConstants";
import ErrorBoundary from "../../../../util/ErrorBoundary";
import { projectHasADefaultArcgisMap } from "../../../utils";
import { doLogUsage } from "../../admin/restQueries";
import { NoGisGroup, NoGisMap } from "../../gis/ArcGisMap";
import { useGetSingleIssueBoardQuery, useGetSumsByFormulaQuery } from "../IssueBoardRTK";
import { useDoUpdateIssueMutation, useGetUniqueTagsOnBoardQuery } from "../issueRTK";
import {
  selectActiveTableTemplate,
  selectConnectedBimSpacesDisabled,
  selectFilteredIssuesOnBoard,
  selectIssueBoardContainsFormula,
  selectIssueBoardScopedMembers,
  selectIssuesOrTasksOnBoardArgs,
} from "../issueSelectors";
import { setSelectedPnsFilterItemInRedux, thunkOpenIssueModalById, updateColumnStateThunk } from "../issueSlice";
import { loadIssueTableTemplateThunk } from "../issueThunk";
import { getInitialIssueTableTemplate } from "../issueUtils";
import { selectIssueBoardId } from "../redux/selectors/issueCommonSelectors";
import IssueDashboardMain from "./Dashboard/IssueDashboardMain";
import { IssueDbFilterBarNew } from "./filters/IssueFilterBarNew";
import IssueFloorplan from "./Floorplan/IssueFloorplan";
import SvarGantt from "./Gant/GanttChart/SvarGantt";
import IssuesGis from "./GIS/IssuesGis";
import IssueTabActionBar from "./IssueTabActionBar";
import IssueKanban from "./Kanban/IssueKanban";
import { Lappeplan } from "./Lappeplan/Lappeplan";
import IssueTableAgGrid from "./Table/IssueTableAgGrid";

const IssueTab: React.FC<{ setActiveTab: (tab: string) => void }> = ({ setActiveTab }) => {
  const [tableTempalteWasSetOnStart, setTableTempalteWasSetOnStart] = useState<boolean>(false);

  const project = useAppSelector((state) => state.adminReducer.project);
  const allColumnState = useAppSelector((state) => state.issueReducerNew.allColumnState);
  const activeTableTemplateId = useAppSelector((state) => state.issueReducerNew.activeTableTemplateId);
  const activeView = useAppSelector((state) => state.issueReducerNew.activeView);
  const selectedPnsFilterItem = useAppSelector((state) => state.issueReducerNew.selectedPnsFilterItem);
  const openedModals = useAppSelector((state) => state.globalReducer.openedAppModals);
  const gridRef = useRef<AgGridReact>(null);
  const pnsItems = project?.pns as FlatHierarchy<IPNSProps>;

  const projectId = useAppSelector(selectCurrentProjectId);
  const issueBoardId = useAppSelector(selectIssueBoardId);
  const issueBoardContaintsFormula = useAppSelector(selectIssueBoardContainsFormula);
  const connectedBimSpacesDisabled = useAppSelector(selectConnectedBimSpacesDisabled);
  const activeTemplate = useAppSelector(selectActiveTableTemplate);
  const boardScopedMembers = useAppSelector(selectIssueBoardScopedMembers);

  const args = useAppSelector(selectIssuesOrTasksOnBoardArgs);

  const {
    isLoading: isLoadingIssues,
    error: errorIssues,
    isFetching: issueFetching,
  } = useGetProjectByProjectIdIssuesOnBoardQuery(args, {
    skip: args.projectId == null || args.issueboardId == null,
  });

  useGetSumsByFormulaQuery(
    {
      projectId: projectId,
      issueBoardId,
    },
    {
      skip: issueBoardContaintsFormula !== true,
    },
  );

  const dispatch = useAppDispatch();
  const location = useLocation();

  const {
    data: issueBoard,
    isLoading: boardLoading,
    error: boardError,
  } = useGetSingleIssueBoardQuery({
    projectId: project._id,
    issueBoardId: issueBoardId,
  });

  useGetMcbimSpacesQuery(
    { projectId },
    {
      skip:
        projectId == null ||
        project?.mcbim?.activated !== true ||
        issueBoard == null ||
        connectedBimSpacesDisabled === true,
    },
  );

  useEffect(() => {
    if (issueBoardId != null && boardLoading === false && project != null && issueBoard != null) {
      doLogUsage(project, MCApplicationsUrlEnum.ISSUE, "open", issueBoard?.issueNameSetting);
    }
  }, [issueBoardId, issueBoard, boardLoading, project]);

  const [updateIssue, { error: updateIssueError }] = useDoUpdateIssueMutation();

  const filtredIssues = useAppSelector(selectFilteredIssuesOnBoard);

  useGetUniqueTagsOnBoardQuery({
    projectId: project._id,
    boardId: issueBoardId,
  });

  useEffect(() => {
    if (
      issueBoard != null &&
      activeTableTemplateId == null &&
      tableTempalteWasSetOnStart === false
    ) {
      const activeTableTemplate = getInitialIssueTableTemplate(
        issueBoard.tableTemplates ?? [],
        issueBoard.defaultTemplate,
        location.search,
      );
      if (activeTableTemplate != null) {
        setTableByTableTemplateCb(activeTableTemplate, true);
        setTableTempalteWasSetOnStart(true);
      }
    }
  }, [issueBoard, activeTableTemplateId, location.search, tableTempalteWasSetOnStart]);

  useEffect(() => {
    if (issueBoard != null && issueBoard.defaultTemplate == null && activeTableTemplateId == null) {
      setHideTable(false);
    }
  }, [issueBoard, activeTableTemplateId]);

  //This is for changing template after table loaded. setTableByTableTemplateCb was previously called directly from tableTemplate component
  useEffect(() => {
    if (!issueBoard || !activeTemplate) return;
    const searchParams = new URLSearchParams(history.location.search);
    if (!searchParams.has("id")) {
      searchParams.set("template", activeTemplate._id);
      history.push(`${history.location.pathname}?${searchParams.toString()}`);
    }
    setHideTable(true);
    setTimeout(() => {
      setHideTable(false);
    }, 500);
  }, [activeTemplate?._id]);

  let history = useHistory();

  useEffect(() => {
    const id = new URLSearchParams(history.location.search).get("id");

    if (id?.length === 24 && openedModals?.find((item) => item.itemId === id) == null) {
      dispatch(
        addAppModalThunk({
          itemId: id,
          boardId: issueBoardId,
          projectId: project._id,
          app: AppModalEnums.ISSUE,
        }),
      );
    }
  }, [dispatch, history.location.search, issueBoardId, openedModals, project._id]);

  const setIssueIdUrlParam = (issue: IIssueApi) => {
    if (issue != null) {
      dispatch(thunkOpenIssueModalById(issue._id));
    } else {
      history.push(`${history.location.pathname}`);
    }
  };

  const [hideTable, setHideTable] = useState<boolean>(true);

  const setTableByTableTemplateCb = async (tableTemplate: ITableTemplate, onLoad?: boolean) => {
    setHideTable(true);
    const searchParams = new URLSearchParams(history.location.search);

    if (!searchParams.has("id")) {
      searchParams.set("template", tableTemplate._id);
      history.replace(`${history.location.pathname}?${searchParams.toString()}`);
    }

    if (onLoad !== true || (allColumnState[issueBoardId] == null && tableTemplate._id === issueBoard.defaultTemplate)) {
      //if template is clicked, or no column state is saved and default template is active, save the new column state
      const { tableColConfigs, tableColumns = [] } = tableTemplate;
      const columnsToSave = tableColConfigs?.length ? tableColConfigs : tableColumns.map((col) => ({ colId: col }));
      dispatch(updateColumnStateThunk(columnsToSave));
    }

    dispatch(loadIssueTableTemplateThunk(tableTemplate));

    setTimeout(() => {
      setHideTable(false);
    }, 500);
  };

  const isLoading = boardLoading || isLoadingIssues || issueFetching;

  const error = boardError || updateIssueError || errorIssues;

  const selectedPnsIds = useMemo(() => {
    const id = pnsItems.find((item) => item.props.pnsId === selectedPnsFilterItem)?._id;
    return id != null ? [id] : [];
  }, [selectedPnsFilterItem, pnsItems]);
  const selectedGisMap = useAppSelector((state) => state.issueReducerNew.selectedGisMap);

  return (
    <>
      {error != null && <FailedAlert />}
      <>
        <IssueTabActionBar
          isLoading={isLoading}
          issueBoard={issueBoard}
          setActiveTab={setActiveTab}
          setTableByTableTemplateCb={setTableByTableTemplateCb}
          gridRef={gridRef}
        />
        <IssueDbFilterBarNew />
        <div
          className="d-flex w-100 py-2"
          style={{
            flex: 1,
            minHeight: 0,
          }}
        >
          {pnsItems?.length > 0 && (
            <HierarchyTreeSelector
              isCollapsed
              style={{
                minWidth: "200px",
                maxWidth: "300px",
                background: "#ededed",
              }}
              isVisible
              nodeList={pnsItems}
              idProp={"pnsId"}
              titleProp={"title"}
              onSelectionChange={(node) => dispatch(setSelectedPnsFilterItemInRedux(node[0]?.props?.pnsId))}
              selectedNodeIds={selectedPnsIds}
            />
          )}
          <>
            {activeView === "TABLE" && true && hideTable !== true && issueBoard != null && (
              <>
                <div className="d-flex flex-column w-100 px-1">
                  <IssueTableAgGrid
                    key={issueBoardId ?? "no-board"}
                    loading={isLoading}
                    items={filtredIssues}
                    gridRef={gridRef}
                    columnProps={{
                      issueBoard: issueBoard,
                      boardBuckets: issueBoard?.buckets ?? [],
                      setSelectedIssue: setIssueIdUrlParam,
                      types: issueBoard?.types.map((type) => type.name),
                      pnsItems: pnsItems,
                      members: boardScopedMembers as unknown as IProjectMemberApiItem[],
                      projectId: project._id,
                      issueBoardId: issueBoardId,
                      otherAttributesConfig: issueBoard?.otherAttributesConfig,
                      otherAttributesTasksConfig: issueBoard?.otherAttributesTasksConfig,
                      updateItemMembers: (itemId: string, members: IMinUserDepricated[]) => {
                        updateIssue({
                          projectId: project._id,
                          issueBoardId: issueBoardId,
                          issueId: itemId,
                          attr: "itemMembers",
                          value: members,
                          oldValue: "",
                        });
                      },
                    }}
                  />
                </div>
              </>
            )}
            {activeView === "KANBAN" && <IssueKanban />}
            {activeView === "NOTEBOARD" && (
              <ReactFlowProvider>
                <Lappeplan />
              </ReactFlowProvider>
            )}
            {activeView === "GIS" && (
              <>
                {selectedGisMap?.mapId != null && (
                  <IssuesGis
                    issues={filtredIssues}
                    projectId={project._id}
                    mapId={selectedGisMap?.mapId}
                    boardId={issueBoardId}
                  />
                )}
                {project.arcgis?.groupId != null && projectHasADefaultArcgisMap(project) === false && <NoGisMap />}
                {project.arcgis?.groupId == null && <NoGisGroup />}
              </>
            )}
            {activeView === "GANTT" && (
              <ErrorBoundary>
                <div style={{ flexGrow: 1, overflow: "auto" }} className="mx-1">
                  <SvarGantt />
                </div>
              </ErrorBoundary>
            )}
            {activeView === IssueViewEnum.BIM && <IssueFloorplan />}
            {activeView === IssueViewEnum.DASHBOARD && (
              <IssueDashboardMain projectId={project._id} issueBoardId={issueBoardId} />
            )}
          </>
        </div>
      </>
    </>
  );
};

export default IssueTab;
